import React, {cloneElement, forwardRef, useRef} from "react";
import {funcOrValOr} from "./CommonJS";
import {classNameScrollbarThin} from "./CommonHtml";
import {initRef, tailCheck, transitions, useStateEx} from "./CommonReact";
import {Button} from "./Inputs";
import {UIPopPanel} from "./UIPopPanel";
import {InputSearch} from "../app/CommonUIPopups";


export const Menu = forwardRef(({items, dispRow, onSelect, className, classNameItm, itemHighlight, onPreSelect}, ref) => {
    const vals = useRef({}).current;

    function onLayout(el) {
        if (!el) return;
        vals.focusables = [...el.querySelectorAll(".dropdown-btn.cursor-pointer[tabindex]")];
        if (document.activeElement?.tagName !== "INPUT")        // quick hack not to steal focus from input elements.
            vals.focusables[0]?.focus();
        initRef(ref, el);
    }
    const moveTo = to => vals.focusables[to]?.focus();
    const move = (e, dir) => moveTo(vals.focusables.indexOf(e.target) + dir);

    return (
        <div ref={onLayout} className={className}>
            {funcOrValOr(items)?.map((itm, idx) => (
                itm.type === "sep" ? <div key={idx} className="border border-bg1"/>
                    : itm.type === "static" ?
                        <div key={idx} className={`flex rounded-md items-center w-full px-4 py-2 text-sm ${itm.className}`}>{itm.h}</div>
                        : <Button
                            key={idx} disabled={itm.disabled} onClick={e => {
                                e.stopPropagation();
                                onPreSelect?.();
                                (itm.a || onSelect)?.(itm, idx);
                            }} {...itm.props}
                            data-test={typeof itm.h === "string" ? itm.h.replace(/ /g, "-").toLowerCase() : `menu-item-${idx}`}
                            className={tailCheck(itm.className + " " + classNameItm, "mr-", "dropdown-btn", "rounded-md", "items-center gap-2",
                                                 "w-full", "px-3 py-1", itm.disabled ? "opacity-40 cursor-default" : "hover:bg-divider",
                                                 "focus-visible:bg-divider outline-none", itemHighlight === idx && "bg-prm-main/40")}
                            onKeys={{
                                ArrowUp: e => move(e, -1), ArrowDown: e => move(e, 1),
                                Home: () => moveTo(0), End: () => moveTo(vals.focusables.length - 1)
                            }}>
                            {dispRow?.(itm, idx) ?? <>
                                {typeof itm.i !== "string" ? itm.i : <span className={`${itm.i} opacity-70`}/>}
                                {itm.h}
                            </>}
                        </Button>
            ))}
        </div>
    );
});


export const PopupMenu = ({items = undefined, dispRow = undefined, onSelect = undefined, classNameItm = undefined, itemHighlight = undefined,
    refMenu = undefined, header = undefined, footer = undefined, classNamePanel = "", ...props}) =>
    <PopupPanel
        {...props} onShowPanel={({closePanel}) =>
            <div className={tailCheck(classNamePanel, "bg-popup fill-subtitle1 text-title", "rounded-md", "shadow-lg ring-opacity-5 focus:outline-none")}>
                {funcOrValOr(header)}
                <Menu
                    ref={refMenu} onPreSelect={closePanel}
                    className={`rounded-md overflow-auto max-h-[50vh] whitespace-pre ${classNameScrollbarThin}`}
                    {...{items, dispRow, classNameItm, itemHighlight, onSelect}}/>
                {funcOrValOr(footer)}
            </div>
        }/>;


export const PopupMenuSearch = ({panelProps = undefined, ...searchMenuProps}) =>
    <PopupPanel {...panelProps} onShowPanel={({closePanel}) => <InputSearchInPopup {...{closePanel, ...searchMenuProps}}/>}/>;

function InputSearchInPopup({closePanel, items, pred, classNamePanel, refMenu, footer, ...menuProps}) {
    const {state: st, onChange} = useStateEx({search: ""});
    items = !items || !st.search ? items : items.filter(e => pred(e, st.search));
    return (
        <div className={tailCheck(classNamePanel, "bg-popup fill-subtitle1 text-title", "rounded-md", "shadow-lg ring-opacity-5 focus:outline-none")}>
            <div className="flex justify-end px-2.5 py-1">
                <InputSearch search={st.search} onChange={onChange}/>
            </div>
            <Menu
                ref={refMenu} onPreSelect={closePanel}
                className={`rounded-md overflow-auto max-h-[50vh] whitespace-pre ${classNameScrollbarThin}`}
                {...{items, ...menuProps}}/>
            {funcOrValOr(footer)}
        </div>
    );
}


// A button popping up a panel.
export function PopupPanel({button, align = "bottom-start", transition = transitions.wipeDownHide, margin = 10, onShowPanel, ctrl, ...props}) {
    const rAnchor = useRef(), locCtrl = useRef({}).current;
    ctrl ||= locCtrl;

    // add onClick and ref to provided button element
    return (<>
        {button && cloneElement(button, {onClick: e => {e.stopPropagation(); ctrl.set(cur => ({show: !cur.show}));}, ref: rAnchor})}
        <UIPopPanel {...{ctrl, rAnchor, align, transition, margin, ...props}} children={onShowPanel}/>
    </>);
}


export function OptsPicker({align = "bottom-start", opts, button, dispPopOpt, classNamePanel}) {
    function dispOptsPanel() {
        const vOpts = typeof opts !== "function" ? opts : opts();

        const dispOptsArr = opts =>
            <div className="p-1 flex items-center justify-between flex-wrap">
                {opts.map(opt => <div key={opt} className={`m-1.5`}>{dispPopOpt(opt)}</div>)}
            </div>;

        return typeof vOpts[0] !== "object" ? dispOptsArr(vOpts) :
            vOpts.map(({h, v}, i) =>
                <div key={i} className="flex flex-col opacity-90">
                    <div className="px-2 pt-1 text-sm text-white">{typeof h === "string" ? h + ":" : h}</div>
                    {dispOptsArr(v)}
                </div>
            );
    }

    return (
        <PopupPanel
            {...{button, align}} onShowPanel={() =>
                <div className={`overflow-y-auto max-h-72 whitespace-pre rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${(!classNamePanel?.match(/(^| )max-w-/)) && "max-w-2xl"} ${(!classNamePanel?.match(/(^| )bg-/)) && "bg-white dark:bg-gray-800/95 dark:text-gray-300"} ${classNamePanel}`}>
                    {dispOptsPanel()}
                </div>
            }/>
    );
}

export function ReadMorePopUpWrapper({children, numToShow = 3}) {
    return <>
        {children.length > numToShow &&
            <PopupPanel
                button={
                    <Button
                        h="+" data-test="see-more-btn"
                        className="border-prm-light min-w-0 shrink-0 aspect-square border text-prm-light border-dashed rounded-full shadow-lg px-1.5 text-xs"/>}
                onShowPanel={() =>
                    <div className="bg-bg3 gap-2 rounded-lg p-1 flex flex-col" data-test="see-more-popup">
                        {children.slice(numToShow)}
                    </div>}/>}
        {children.slice(0, numToShow)}
    </>;
}
